import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

import { CalculatedVariable } from 'src/app/models/calculated-variable';
import { AppState } from 'src/app/state';
import { selectSelectedStudy } from 'src/app/dashboard/state/study/study.selectors';
import { Study } from 'src/app/models/study';
import { Module } from 'src/app/models/module';
import { ModuleItem } from 'src/app/models/module-item';
import { CALCULATED_VARIABLE_FUNCTIONS } from 'src/app/core/dashboard-constants';
import {
  createCalculatedVariable,
  updateCalculatedVariable,
} from 'src/app/dashboard/state/calculated-variable/calculated-variable.actions';

@Component({
  templateUrl: './calculated-variable-dialog.component.html',
  styleUrls: ['./calculated-variable-dialog.component.scss'],
})
export class CalculatedVariableDialogComponent implements OnInit, OnDestroy {
  private subscriptions = new Subscription();

  calculatedVariable: CalculatedVariable;
  isEdit = false;
  calculatedVariableForm: FormGroup;

  selectedStudy: Study;

  calculationNames: string[] = [];
  calculationNamesWithModuleItemIds: {
    calculationName: string;
    moduleItemId: number;
  }[] = [];
  dataSourceCalculationNames: MatTableDataSource<string>;
  columnsToDisplayCalculationNamesTable = ['uniqueName'];

  calculatedVariableFunctions = CALCULATED_VARIABLE_FUNCTIONS;
  dataSourceCalculationFunctions: MatTableDataSource<any> = new MatTableDataSource(this.calculatedVariableFunctions);
  columnsToDisplayCalculationFunctionsTable = ['id', 'description'];

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { calculatedVariable: CalculatedVariable },
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.calculatedVariableForm = this.formBuilder.group({
      uniqueName: ['', [Validators.required, Validators.pattern('[a-zA-Z0-9]*')]],
      description: ['', [Validators.required]],
      formula: ['', [Validators.required]],
    });

    this.calculatedVariable = this.data?.calculatedVariable;
    this.isEdit = !!this.data?.calculatedVariable;

    this.subscriptions.add(
      this.store.select(selectSelectedStudy).subscribe((study) => {
        this.selectedStudy = study;
        this.selectedStudy.modules?.forEach((module: Module) => {
          module.moduleItems?.forEach((moduleItem: ModuleItem) => {
            if (moduleItem.forCalculation) {
              this.calculationNamesWithModuleItemIds.push({
                calculationName: moduleItem.calculationName,
                moduleItemId: moduleItem.id,
              });
            }
          });
        });
      })
    );

    this.calculationNames = this.calculationNamesWithModuleItemIds.map((variable) => variable.calculationName);
    this.dataSourceCalculationNames = new MatTableDataSource(this.calculationNames);
    this.dataSourceCalculationNames.filterPredicate = (uniqueName, filter) => {
      return uniqueName.toLowerCase().includes(filter);
    };
  }

  get uniqueName(): AbstractControl {
    return this.calculatedVariableForm.get('uniqueName');
  }

  get description(): AbstractControl {
    return this.calculatedVariableForm.get('description');
  }

  get formula(): AbstractControl {
    return this.calculatedVariableForm.get('formula');
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSourceCalculationNames.filter = filterValue.trim().toLowerCase();

    //Update CalculationNames
    this.calculationNames = this.calculationNamesWithModuleItemIds.map((variable) => variable.calculationName);
    this.calculationNames = this.calculationNames.filter(s => s.includes(filterValue.trim().toLowerCase()));
  }

  dropCalculationName(event: CdkDragDrop<string>) {
    if (event.isPointerOverContainer) {
      return;
    }
    const calculationNameToDrop = event.previousContainer.data[event.previousIndex];
    this.calculatedVariableForm.setValue({
      uniqueName: this.uniqueName.value,
      description: this.description.value,
      formula: this.formula.value + calculationNameToDrop,
    });
  }

  dropCalculationFunction(event: CdkDragDrop<any>) {
    if (event.isPointerOverContainer) {
      return;
    }
    const calculationFunctionToDrop = event.previousContainer.data[event.previousIndex];
    this.calculatedVariableForm.setValue({
      uniqueName: this.uniqueName.value,
      description: this.description.value,
      formula: this.formula.value + calculationFunctionToDrop.calculationName,
    });
  }

  submitCalculatedVariable() {
    if (this.calculatedVariableForm.invalid) {
      this.toastrService.error('Id, description and calculate logic are required!');
      return;
    }

    const moduleItems = [];
    this.calculationNamesWithModuleItemIds.forEach((variable) => {
      if (this.formula.value.indexOf(variable.calculationName) !== -1) {
        moduleItems.push(variable.moduleItemId);
      }
    });

    const calculatedVariable: CalculatedVariable = {
      ...this.calculatedVariableForm.value,
      studyId: this.selectedStudy.id,
      moduleItems,
    };

    if (this.isEdit) {
      calculatedVariable.id = this.calculatedVariable.id;
      this.store.dispatch(updateCalculatedVariable({ calculatedVariable }));
    } else {
      this.store.dispatch(createCalculatedVariable({ calculatedVariable }));
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
